import type { PartialBlock } from '@blocknote/core';
import type { SyncDelta } from '@couplespace/sync';

import type { BlockRecord } from '@/lib/blocks/types';

import { blockContentToStored, type FlatBlock, type StoredBlockContent } from './blocknote-types';

/** Same shape as the editor's `SyncSnapshot`: what the server holds as of the last save or patch. */
export type RemoteSnapshot = {
  flat: FlatBlock[];
  syncVersions: Map<string, number>;
};

export type RemotePatchOp =
  | { kind: 'update'; id: string; block: PartialBlock }
  | { kind: 'insert'; block: PartialBlock; reference: { id: string; placement: 'before' | 'after' } }
  | { kind: 'remove'; ids: string[] };

export type RemotePatchPlan = {
  ops: RemotePatchOp[];
  /** Server version for every block the patch brings in line with the server. */
  versions: Map<string, number>;
  /** Server `position` for every block a delta reported. */
  serverPositions: Map<string, number>;
  /** Skipped because the block is focused or has unsaved edits; retry them later. */
  deferred: SyncDelta[];
  /** The page row itself changed (title, icon). */
  pageRow: BlockRecord | null;
  /** The change cannot be patched in place (move, reorder, unknown order); reload the page. */
  needsReload: boolean;
};

type RemoteRow = {
  id: string;
  parentId: string;
  position: number;
  type: string;
  content: StoredBlockContent;
  syncVersion: number;
};

const NON_CONTENT_TYPES = new Set(['page', 'database']);
/** Stored without a usable URL; the page load path presigns them. */
const FILE_TYPES = new Set(['image', 'file']);

function sameContent(
  a: { type: string; content: StoredBlockContent },
  b: { type: string; content: StoredBlockContent },
): boolean {
  return a.type === b.type && JSON.stringify(a.content) === JSON.stringify(b.content);
}

function toRemoteRow(delta: SyncDelta, pageId: string): RemoteRow | null {
  const payload = delta.payload;
  if (!payload || payload.deletedAt) {
    return null;
  }
  const content =
    payload.content && typeof payload.content === 'object'
      ? (payload.content as Record<string, unknown>)
      : {};
  return {
    id: delta.rowId,
    parentId: typeof payload.parentId === 'string' ? payload.parentId : pageId,
    position: typeof payload.position === 'number' ? payload.position : 0,
    type: typeof payload.type === 'string' ? payload.type : 'paragraph',
    content: blockContentToStored(content),
    syncVersion: typeof payload.syncVersion === 'number' ? payload.syncVersion : 0,
  };
}

/** Without `children`, `editor.updateBlock` keeps the block's existing children. */
function toPartialBlock(row: RemoteRow, children?: PartialBlock[]): PartialBlock {
  return {
    id: row.id,
    type: row.type,
    props: row.content.props ?? {},
    content: row.content.content,
    ...(children ? { children } : {}),
  } as PartialBlock;
}

function emptyPlan(): RemotePatchPlan {
  return {
    ops: [],
    versions: new Map(),
    serverPositions: new Map(),
    deferred: [],
    pageRow: null,
    needsReload: false,
  };
}

function reloadPlan(pageRow: BlockRecord | null): RemotePatchPlan {
  return { ...emptyPlan(), pageRow, needsReload: true };
}

/**
 * Turn live `blocks` deltas into in-place editor operations.
 *
 * - Echoes of our own saves (server version at or below the snapshot's) are skipped.
 * - Blocks with unsaved local edits, and the focused block's content, are
 *   deferred: retried once the cursor leaves or the edit is saved (a saved edit
 *   wins through the `expectedSyncVersion` conflict path).
 * - Sibling order uses server positions (`serverPositions` plus the deltas), so
 *   agent inserts with fractional positions land where the server has them.
 * - A move, a reorder of existing blocks, a remote image/file change or a
 *   sibling with no known server position falls back to a reload.
 */
export function planRemotePatch(input: {
  pageId: string;
  snapshot: RemoteSnapshot;
  localFlat: FlatBlock[];
  serverPositions: ReadonlyMap<string, number>;
  deltas: SyncDelta[];
  focusedBlockId: string | null;
}): RemotePatchPlan {
  const { pageId, snapshot, localFlat, serverPositions, deltas, focusedBlockId } = input;

  const latest = new Map<string, SyncDelta>();
  for (const delta of deltas) {
    if (delta.table !== 'blocks') {
      continue;
    }
    const prev = latest.get(delta.rowId);
    if (!prev || delta.seq > prev.seq) {
      latest.set(delta.rowId, delta);
    }
  }

  const plan = emptyPlan();
  const snapById = new Map(snapshot.flat.map((block) => [block.id, block]));
  const localById = new Map(localFlat.map((block) => [block.id, block]));
  const removeIds: string[] = [];
  const touchedKnown = new Map<string, RemoteRow>();
  const movedKnown = new Set<string>();
  const inserts = new Map<string, RemoteRow>();

  for (const delta of latest.values()) {
    if (delta.rowId === pageId) {
      if (!delta.payload || delta.payload.deletedAt) {
        return reloadPlan(null);
      }
      plan.pageRow = delta.payload as unknown as BlockRecord;
      continue;
    }

    const known = snapById.get(delta.rowId);
    const row = toRemoteRow(delta, pageId);

    if (!row) {
      if (known && localById.has(delta.rowId)) {
        removeIds.push(delta.rowId);
      }
      continue;
    }

    if (NON_CONTENT_TYPES.has(row.type)) {
      continue;
    }

    if (known) {
      if (row.syncVersion <= (snapshot.syncVersions.get(row.id) ?? 0)) {
        continue;
      }
      if (row.parentId !== known.parentId || FILE_TYPES.has(row.type) || FILE_TYPES.has(known.type)) {
        return reloadPlan(plan.pageRow);
      }
      const local = localById.get(row.id);
      if (!local) {
        continue;
      }
      plan.serverPositions.set(row.id, row.position);
      movedKnown.add(row.id);
      const locallyDirty = local.parentId !== known.parentId || !sameContent(local, known);
      if (locallyDirty || (row.id === focusedBlockId && !sameContent(row, known))) {
        plan.deferred.push(delta);
        continue;
      }
      touchedKnown.set(row.id, row);
      continue;
    }

    if (!localById.has(row.id)) {
      if (FILE_TYPES.has(row.type)) {
        return reloadPlan(plan.pageRow);
      }
      inserts.set(row.id, row);
    }
  }

  // Inserted children of inserted blocks travel inside their parent.
  const insertChildren = new Map<string, RemoteRow[]>();
  const rootInserts: RemoteRow[] = [];
  for (const row of inserts.values()) {
    if (inserts.has(row.parentId)) {
      const list = insertChildren.get(row.parentId) ?? [];
      list.push(row);
      insertChildren.set(row.parentId, list);
    } else {
      rootInserts.push(row);
    }
  }
  const buildInsertTree = (row: RemoteRow): PartialBlock => {
    const children = (insertChildren.get(row.id) ?? []).sort((a, b) => a.position - b.position);
    for (const child of children) {
      plan.versions.set(child.id, child.syncVersion);
      plan.serverPositions.set(child.id, child.position);
    }
    return toPartialBlock(row, children.map(buildInsertTree));
  };

  const removed = new Set(removeIds);
  const hasRemovedAncestor = (id: string): boolean => {
    for (let parent = snapById.get(id)?.parentId; parent; parent = snapById.get(parent)?.parentId) {
      if (removed.has(parent)) {
        return true;
      }
    }
    return false;
  };
  const affectedParents = new Set<string>();
  for (const id of movedKnown) {
    affectedParents.add(snapById.get(id)!.parentId);
  }
  for (const row of rootInserts) {
    affectedParents.add(row.parentId);
  }

  const insertOps: RemotePatchOp[] = [];
  for (const parentId of affectedParents) {
    if (
      parentId !== pageId &&
      (!snapById.has(parentId) ||
        !localById.has(parentId) ||
        removed.has(parentId) ||
        hasRemovedAncestor(parentId))
    ) {
      return reloadPlan(plan.pageRow);
    }

    const siblings: Array<{ id: string; position: number; inserted: boolean }> = [];
    for (const block of snapshot.flat) {
      if (block.parentId !== parentId || removed.has(block.id)) {
        continue;
      }
      const position = plan.serverPositions.get(block.id) ?? serverPositions.get(block.id);
      if (position === undefined) {
        return reloadPlan(plan.pageRow);
      }
      siblings.push({ id: block.id, position, inserted: false });
    }
    for (const row of rootInserts) {
      if (row.parentId === parentId) {
        siblings.push({ id: row.id, position: row.position, inserted: true });
      }
    }
    // An editor client creates a block at index i before it renumbers the
    // siblings, so on a tie the new block goes first.
    siblings.sort((a, b) => a.position - b.position || Number(b.inserted) - Number(a.inserted));

    const serverOrder = siblings.filter((s) => !s.inserted && localById.has(s.id)).map((s) => s.id);
    const knownIds = new Set(serverOrder);
    const localOrder = localFlat
      .filter((block) => block.parentId === parentId && knownIds.has(block.id))
      .map((block) => block.id);
    if (serverOrder.join() !== localOrder.join()) {
      return reloadPlan(plan.pageRow);
    }

    const present = new Set(serverOrder);
    for (let index = 0; index < siblings.length; index += 1) {
      const sibling = siblings[index]!;
      if (!sibling.inserted) {
        continue;
      }
      const row = inserts.get(sibling.id)!;
      let reference: { id: string; placement: 'before' | 'after' } | null = null;
      for (let back = index - 1; back >= 0 && !reference; back -= 1) {
        if (present.has(siblings[back]!.id)) {
          reference = { id: siblings[back]!.id, placement: 'after' };
        }
      }
      for (let ahead = index + 1; ahead < siblings.length && !reference; ahead += 1) {
        if (present.has(siblings[ahead]!.id)) {
          reference = { id: siblings[ahead]!.id, placement: 'before' };
        }
      }
      if (!reference) {
        return reloadPlan(plan.pageRow);
      }
      insertOps.push({ kind: 'insert', block: buildInsertTree(row), reference });
      plan.versions.set(row.id, row.syncVersion);
      plan.serverPositions.set(row.id, row.position);
      present.add(row.id);
    }
  }

  const removeRoots = removeIds.filter((id) => !hasRemovedAncestor(id));
  if (removeRoots.length > 0) {
    plan.ops.push({ kind: 'remove', ids: removeRoots });
  }
  for (const row of touchedKnown.values()) {
    if (hasRemovedAncestor(row.id)) {
      continue;
    }
    plan.versions.set(row.id, row.syncVersion);
    if (!sameContent(row, snapById.get(row.id)!)) {
      plan.ops.push({ kind: 'update', id: row.id, block: toPartialBlock(row) });
    }
  }
  plan.ops.push(...insertOps);

  return plan;
}

/**
 * Snapshot after the plan is applied to the editor. Patched blocks take the
 * editor's own (normalised) form, and blocks that were in sync take their new
 * editor index, so the next save pushes only genuine local edits.
 */
export function applyRemotePatchToSnapshot(input: {
  snapshot: RemoteSnapshot;
  plan: RemotePatchPlan;
  localFlatBefore: FlatBlock[];
  editorFlatAfter: FlatBlock[];
}): RemoteSnapshot {
  const { snapshot, plan, localFlatBefore, editorFlatAfter } = input;
  const removedRoots = new Set(plan.ops.flatMap((op) => (op.kind === 'remove' ? op.ids : [])));
  const parentOf = new Map(snapshot.flat.map((block) => [block.id, block.parentId]));
  const isRemoved = (id: string): boolean => {
    let current: string | undefined = id;
    while (current) {
      if (removedRoots.has(current)) {
        return true;
      }
      current = parentOf.get(current);
    }
    return false;
  };

  const before = new Map(localFlatBefore.map((block) => [block.id, block]));
  const after = new Map(editorFlatAfter.map((block) => [block.id, block]));
  const syncVersions = new Map(snapshot.syncVersions);
  const flat: FlatBlock[] = [];
  const seen = new Set<string>();

  for (const block of snapshot.flat) {
    if (isRemoved(block.id)) {
      syncVersions.delete(block.id);
      continue;
    }
    seen.add(block.id);
    const fromEditor = after.get(block.id);
    const version = plan.versions.get(block.id);
    if (version !== undefined && fromEditor) {
      syncVersions.set(block.id, version);
      flat.push({ ...fromEditor, syncVersion: version });
      continue;
    }
    const local = before.get(block.id);
    const inSyncPosition =
      local !== undefined && local.parentId === block.parentId && local.position === block.position;
    if (inSyncPosition && fromEditor && fromEditor.parentId === block.parentId) {
      flat.push({ ...block, position: fromEditor.position });
      continue;
    }
    flat.push(block);
  }

  for (const [id, version] of plan.versions) {
    if (seen.has(id)) {
      continue;
    }
    const fromEditor = after.get(id);
    if (!fromEditor) {
      continue;
    }
    syncVersions.set(id, version);
    flat.push({ ...fromEditor, syncVersion: version });
  }

  return { flat, syncVersions };
}

/** Server positions written by a save: new blocks and blocks whose parent or index changed. */
export function serverPositionsAfterSave(
  previous: RemoteSnapshot,
  next: RemoteSnapshot,
): Map<string, number> {
  const prevById = new Map(previous.flat.map((block) => [block.id, block]));
  const written = new Map<string, number>();
  for (const block of next.flat) {
    const prev = prevById.get(block.id);
    if (!prev || prev.position !== block.position || prev.parentId !== block.parentId) {
      written.set(block.id, block.position);
    }
  }
  return written;
}
